import { useCallback, useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Coins, Undo2 } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { formatCurrency, formatDate } from "@/lib/format";
import {
  EmptyState,
  ErrorState,
  Eyebrow,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { components } from "@dolfin/api-public";

type LedgerEntry = components["schemas"]["SupplierCreditLedgerEntryResponse"];
type Balance = components["schemas"]["SupplierCreditBalanceResponse"];
type EntryType = components["schemas"]["SupplierCreditLedgerEntryType"];

const PAGE_SIZE = 20;

const ENTRY_META: Record<EntryType, { label: string; Icon: typeof ArrowUpRight }> = {
  CreditNoteIssued: { label: "Credit note issued", Icon: ArrowUpRight },
  AppliedToBill: { label: "Applied to bill", Icon: ArrowDownLeft },
  ApplicationReversed: { label: "Application reversed", Icon: Undo2 },
};

export interface SupplierCreditTabProps {
  supplierId: string;
  onBillClick?: (billId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
}

export function SupplierCreditTab({
  supplierId,
  onBillClick,
  onCreditNoteClick,
}: SupplierCreditTabProps) {
  const api = useDolfinClient();
  const [balances, setBalances] = useState<Balance[]>([]);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const fetchBalances = useCallback(async () => {
    const { data } = await api.GET("/v1/suppliers/{supplierId}/credit", {
      params: { path: { supplierId } },
    });
    setBalances((data ?? []).filter((b) => Number(b.available ?? 0) !== 0));
  }, [api, supplierId]);

  const fetchLedger = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (cursor) query.Cursor = cursor;
      const { data, error: apiError } = await api.GET("/v1/suppliers/{supplierId}/credit/ledger", {
        params: { path: { supplierId }, query },
      });
      if (apiError || !data) {
        setError("Failed to load credit ledger");
      } else {
        setEntries(data.data ?? []);
        const next = data.meta?.cursors?.next ?? null;
        setNextCursor(next);
        setHasMore(next !== null);
      }
      setLoading(false);
    },
    [api, supplierId],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchBalances();
    fetchLedger();
  }, [fetchBalances, fetchLedger]);

  const currentPage = cursorStack.length + 1;

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchLedger(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchLedger(prevCursor);
  }

  return (
    <div className="space-y-4 pt-2">
      <div className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
        <Eyebrow>Available credit</Eyebrow>
        {balances.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">No credit available with this supplier.</p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-x-8 gap-y-2">
            {balances.map((b) => (
              <p
                key={b.currency}
                className="text-2xl font-bold tracking-tight text-emerald-700 tabular-nums dark:text-emerald-400"
              >
                {formatCurrency(b.available, b.currency)}
              </p>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => fetchLedger(cursorStack[cursorStack.length - 1])} />
      ) : loading ? (
        <LoadingState label="Loading credit ledger..." />
      ) : entries.length === 0 ? (
        <EmptyState icon={Coins} title="No credit movements yet">
          Issued credit notes and their applications to bills appear here.
        </EmptyState>
      ) : (
        <div>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Date</TableHead>
                  <TableHead className={TH}>Movement</TableHead>
                  <TableHead className={TH}>Reference</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {entries.map((e) => (
                  <LedgerRow
                    key={`${e.entryType}-${e.applicationId ?? e.supplierCreditNoteId}-${e.occurredAt}`}
                    entry={e}
                    onBillClick={onBillClick}
                    onCreditNoteClick={onCreditNoteClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </div>
      )}
    </div>
  );
}

function LedgerRow({
  entry,
  onBillClick,
  onCreditNoteClick,
}: {
  entry: LedgerEntry;
  onBillClick?: (billId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
}) {
  const meta = ENTRY_META[entry.entryType ?? "CreditNoteIssued"];
  const amount = Number(entry.amount ?? 0);
  const negative = amount < 0;

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums">
        {formatDate(entry.occurredAt)}
      </TableCell>
      <TableCell>
        <span className="inline-flex items-center gap-1.5">
          <meta.Icon className="size-3.5 text-muted-foreground" />
          {meta.label}
        </span>
      </TableCell>
      <TableCell>
        <ReferenceCell
          entry={entry}
          onBillClick={onBillClick}
          onCreditNoteClick={onCreditNoteClick}
        />
      </TableCell>
      <TableCell
        className={cn(
          "text-right font-medium tabular-nums",
          negative ? "text-muted-foreground" : "text-emerald-700 dark:text-emerald-400",
        )}
      >
        {negative ? "−" : "+"}
        {formatCurrency(Math.abs(amount), entry.currency)}
      </TableCell>
    </TableRow>
  );
}

function ReferenceCell({
  entry,
  onBillClick,
  onCreditNoteClick,
}: {
  entry: LedgerEntry;
  onBillClick?: (billId: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
}) {
  const linkClass = "font-medium text-accent-blue underline-offset-2 hover:underline";

  if (entry.entryType === "CreditNoteIssued") {
    const label = entry.reference || "Credit note";
    if (onCreditNoteClick && entry.supplierCreditNoteId) {
      return (
        <button
          type="button"
          className={linkClass}
          onClick={() => onCreditNoteClick(entry.supplierCreditNoteId!)}
        >
          {label}
        </button>
      );
    }
    return <span>{label}</span>;
  }

  // Applied / reversed: reference the bill it netted against.
  if (onBillClick && entry.billId) {
    return (
      <button type="button" className={linkClass} onClick={() => onBillClick(entry.billId!)}>
        View bill
      </button>
    );
  }
  return <span className="text-muted-foreground">Bill</span>;
}
